import {Grid, Stack, Typography} from "@mui/material";
import * as React from "react"
import {FC, useMemo, useRef} from "react"
import {useTranslation} from "react-i18next";
import {IBookingDetails, IRaDetails} from "../page/contract/ContractInterfaces";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import {ColDef, GridOptions, IDetailCellRendererParams} from "ag-grid-enterprise";
import {AgGridReact} from "ag-grid-react";
import EmptyState from "../../../util/component/EmptyState";
import {formatDate} from "../../finance/utils/FormatUtils";
import {ICellRendererParams} from "ag-grid-community";
import {useHasScope} from "../../../config/globalConfig";
import ActionButtonWithTooltip, {ActionTypes} from "./ActionButtonWithTooltip";
import {
    AutorenewRounded,
    BuildRounded,
    CloudSyncRounded,
    FastForwardRounded,
    HealthAndSafety,
    MoveDownRounded,
    StarRounded,
    StoreRounded
} from "@mui/icons-material";

const BookingAndRaTable:FC<{ data: IBookingDetails[], showTypography?: boolean }> = ({data,showTypography}) => {
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'bookingAndRa'});
    const gridRef = useRef<AgGridReact>(null);
    const linkToBookingDetail = "/booking/view/";
    const linkToRaDetail = "/network/ra-detail/";
    const SCOPE_BOOKING = "SCOPE_SM_REDIRECTING_FROM_SM_BOOKING_LIST";
    const SCOPE_RA_INFO = "SCOPE_SM_RA_VIEW_DETAIL";
    const hasScope = useHasScope;

    const hasRaDetails = (row?: IBookingDetails) => (row?.raDetails?.length ?? 0) > 0;

    enum VipType {
        No = 'NO',
        Internal = 'INTERNAL',
        External = 'EXTERNAL',
        VipCS = 'VIP_CS'
    }


    const columns: ColDef[] = [
        {
            field: "bookingId",
            headerName: t('booking.bookingId'),
            cellRendererSelector: (params) => {
                if (!hasRaDetails(params.data)) {
                    return;
                }
                return {
                    component: 'agGroupCellRenderer'
                };
            },
            unSortIcon: true,
        },
        {
            field: 'dropOffDatetimeForReplacement',
            headerName: t('booking.dropOffDatetimeForReplacement'),
            cellRenderer: (params: any) => params?.value ? formatDate(params.value) : "--",
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'pickUpDate',
            headerName: t('booking.pickUpDate'),
            cellRenderer: (params: any) => params?.value ? formatDate(params.value) : "",
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'dropOffDate',
            headerName: t('booking.dropOffDate'),
            cellRenderer: (params: any) => params?.value ? formatDate(params.value) : "",
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'subscriptionMonth',
            headerName: t('booking.subscriptionMonth'),
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'bookedDays',
            headerName: t('booking.bookedDays'),
            unSortIcon: true,
            sortable: true,
        },
        {
            field: 'status',
            headerName: t('booking.status'),
            cellRenderer: (params: any) => params?.value ?
                t('booking.statuses.'+params.value)  +
                (params.data.subStatus ? " - " +  t('booking.subStatus.'+params.data.subStatus) : "")
                : "",
            unSortIcon: true,
            sortable: true,
            minWidth: 150
        },
        {
            field: 'bookingId',
            headerName: t('booking.bookingLink'),
            unSortIcon: true,
            minWidth: 150,
            hide: !hasScope(SCOPE_BOOKING),
            cellRenderer: (params: ICellRendererParams<any>) => {
                return (
                    <Stack direction="row" columnGap={1} alignItems="flex-start" justifyContent="flex-start" >

                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            window.open(linkToBookingDetail+ `${params.value}`, "_blank");
                        }} auth={SCOPE_BOOKING}/>

                        {params.data?.customerAtDesk &&
                            <StoreRounded sx={{marginTop:1}}
                                titleAccess={t('booking.customerAtDesk')}
                                color="secondary"
                            />
                        }
                        {params.data && [VipType.Internal, VipType.External, VipType.VipCS].includes(params.data.vip) &&
                            <StarRounded sx={{marginTop:1}}
                                titleAccess={t('booking.vip')}
                                color="secondary"
                            />
                        }
                        {params.data?.fastLine &&
                            <FastForwardRounded sx={{marginTop:1}}
                                titleAccess={t('booking.fastLine')}
                                color="secondary"
                            />
                        }
                        {params.data?.carSwap &&
                            <CloudSyncRounded sx={{marginTop:1}}
                                titleAccess={t('booking.carSwap')}
                                color="secondary"
                            />
                        }
                        {params.data?.substitution &&
                            <BuildRounded sx={{marginTop:1}}
                                titleAccess={t('booking.substitution')}
                                color="secondary"
                            />
                        }
                        {params.data?.renewal &&
                            <AutorenewRounded sx={{marginTop:1}}
                                titleAccess={t('booking.renewal')}
                                color="secondary"
                            />
                        }
                        {params.data?.recovery &&
                            <HealthAndSafety sx={{marginTop:1}}
                                titleAccess={t('booking.recovery')}
                                color="secondary"
                            />
                        }
                        {params.data?.packageSwap &&
                            <MoveDownRounded sx={{marginTop:1}}
                                titleAccess={t('booking.packageSwap')}
                                color="secondary"
                            />
                        }
                    </Stack>
                );
            }
        }
    ]


    const columnsRA: ColDef[] = [
        {
            field: "raId",
            headerName: t('ra.raId'),
            unSortIcon: true,
            tooltipValueGetter: (params: any) => (params?.value ? params.value : ""),
        },
        {
            field: 'pickUpDMS',
            headerName: t('ra.pickUpDMS'),
            unSortIcon: true
        },
        {
            field: 'pickUpDate',
            headerName: t('ra.pickUpDate'),
            cellRenderer: (params: any) => params?.value ? formatDate(params.value) : "",
            unSortIcon: true,
            minWidth: 180
        },
        {
            field: 'expectedDropOffDate',
            headerName: t('ra.expectedDropOffDate'),
            cellRenderer: (params: any) => params?.value ? formatDate(params.value) : "",
            unSortIcon: true,
            minWidth: 180
        },
        {
            field: 'actualDropOffDate',
            headerName: t('ra.actualDropOffDate'),
            cellRenderer: (params: any) => params?.value ? formatDate(params.value) : "",
            unSortIcon: true,
            minWidth: 180
        },
        {
            field: 'acrissSip',
            headerName: t('ra.acrissSip'),
            unSortIcon: true
        },
        {
            field: 'status',
            headerName: t('ra.status'),
            unSortIcon: true,
            minWidth: 100,
        },
        {
            field: 'raId',
            headerName: t('ra.raLink'),
            unSortIcon: true,
            minWidth: 100,
            tooltipValueGetter: (params: any) => (params?.value ? params.value : ""),
            hide: !hasScope(SCOPE_RA_INFO),
            cellRenderer: (params: ICellRendererParams<any>) => {
                return (
                    <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                        window.open(linkToRaDetail+ `${params.value}`, "_blank");
                    }} auth={SCOPE_RA_INFO}/>
                );
            }
        }
    ]


    const gridOptions: GridOptions<IBookingDetails> = {
        columnDefs: columns,
        suppressRowClickSelection: true,
        masterDetail: true,
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            flex: 1,
            resizable: true,
            wrapHeaderText: true
        },
        animateRows: true,
        getRowHeight: params => {
            const isDetailRow = params.node.detail;
            if (!isDetailRow) { return undefined; }

            const details = params.data?.raDetails ?? [];
            return details.length * 200;
        },
        detailCellRendererParams: {
            detailGridOptions: {
                domLayout: "autoHeight",
                columnDefs: columnsRA,
                defaultColDef: {
                    flex: 1,
                    resizable: true,
                    wrapHeaderText: true
                },
            },
            getDetailRowData: (params) => {
                params.successCallback(params.data?.raDetails ?? []);
            },
        } as IDetailCellRendererParams<IBookingDetails, IRaDetails>
    };

    const rowData = useMemo<IBookingDetails[]>(() => (
        data ?? []
    ), [data]);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const noRowsOverlayComponentParams = useMemo<any>(() => {
        return {
            title: t(" "),
            content: t(" ")
        };
    }, []);

    return <>
        {showTypography &&  <Grid item>
                <Typography>{t('bookingAndRa.title')}</Typography>
            </Grid>
        }
        <Grid container>
            <Grid item xs={12} sx={{padding: "16px"}}>
                <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} rowData={rowData}
                                   noRowsOverlayComponent={noRowsOverlayComponent}
                                   noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                />
            </Grid>
        </Grid>
    </> 
}



export default BookingAndRaTable;